import { cn } from '@/shared/lib'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import type { VideoConferenceCardProps } from '../model'

const conferenceTokens = designTokens.components.videoConferenceCard

const conferenceTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.lg, {
    fontSize: '36px',
    lineHeight: 'normal',
    fontWeight: 600,
  }),
}

const conferenceBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.md),
}

const conferenceFooterStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.additional,
  ...typographyStyle(designTokens.typography.body.base),
}

const detailsTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.xsSemibold),
}

const detailsBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.xs),
}

const ctaStyle = {
  height: conferenceTokens.buttonHeight,
  borderRadius: conferenceTokens.buttonRadius,
  border: 'none',
  backgroundColor: conferenceTokens.buttonBackground,
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.xsSemibold),
}

export function VideoConferenceCard({
  previewSrc,
  previewAlt = '',
  conferenceTitle,
  conferenceDescription,
  footerLabel,
  conferenceDetails,
  ctaLabel,
  onCtaClick,
  onClick,
  className,
}: VideoConferenceCardProps) {
  const isCardClickable = typeof onClick === 'function'

  const containerStyle = {
    width: conferenceTokens.width,
    height: conferenceTokens.height,
    borderRadius: conferenceTokens.radius,
    backgroundColor: designTokens.colors.neutral.grey,
    padding: `${conferenceTokens.paddingY} ${conferenceTokens.paddingX}`,
    gap: conferenceTokens.contentGap,
  }

  const cardContent = (
    <>
      <div
        className="flex h-full flex-col overflow-hidden"
        style={{
          width: conferenceTokens.leftCardWidth,
          borderRadius: conferenceTokens.leftCardRadius,
          backgroundColor: designTokens.colors.background.surface,
        }}
      >
        <img
          src={previewSrc}
          alt={previewAlt}
          className="w-full object-cover"
          style={{
            height: conferenceTokens.leftImageHeight,
            backgroundColor: designTokens.colors.background.surface,
          }}
        />
        <div
          className="flex h-full w-full flex-col justify-between"
          style={{ padding: conferenceTokens.leftContentPadding }}
        >
          <div className="flex w-full flex-col gap-1">
            <p style={conferenceTitleStyle}>{conferenceTitle}</p>
            <p style={conferenceBodyStyle}>{conferenceDescription}</p>
          </div>
          <p style={conferenceFooterStyle}>{footerLabel}</p>
        </div>
      </div>
      <div
        className="flex h-full flex-col justify-between"
        style={{ width: conferenceTokens.rightWidth }}
      >
        <div className="flex w-full flex-col" style={{ gap: conferenceTokens.detailsGap }}>
          {conferenceDetails.map((detail) => (
            <div key={`${detail.title}-${detail.description}`} className="flex w-full flex-col">
              <p style={detailsTitleStyle}>{detail.title}</p>
              <p style={detailsBodyStyle}>{detail.description}</p>
            </div>
          ))}
        </div>
        {isCardClickable ? (
          <span className="inline-flex w-full items-center justify-center" style={ctaStyle}>
            {ctaLabel}
          </span>
        ) : (
          <button
            type="button"
            className="w-full cursor-pointer"
            style={ctaStyle}
            onClick={onCtaClick}
          >
            {ctaLabel}
          </button>
        )}
      </div>
    </>
  )

  if (isCardClickable) {
    return (
      <button
        type="button"
        className={cn('flex shrink-0 cursor-pointer overflow-hidden text-left', className)}
        style={containerStyle}
        onClick={onClick}
      >
        {cardContent}
      </button>
    )
  }

  return (
    <article className={cn('flex shrink-0 overflow-hidden', className)} style={containerStyle}>
      {cardContent}
    </article>
  )
}

VideoConferenceCard.displayName = 'VideoConferenceCard'
